import { Virtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import { ArrowRightSmall, Xmark } from '@peacecover/icon';
import { useChangeCurrentCastRelationship } from '../hooks/useChangeCurrentCastRelationship';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import type { components } from '@pocapan/shared/types/schema';

type CastRelationship = components['schemas']['ApiV1CastRelationship'];

interface PreviousCastRelationshipListProps {
  castRelationships: CastRelationship[];
  currentCastRelationship: CastRelationship | undefined;
  handleCreateCastRelationship: () => void;
  onOpenChangeThirdPersona: () => void;
  onEndReached: () => void;
  hasCastRelationshipTicket: boolean;
}

const PreviousCastRelationshipList = ({
  castRelationships,
  currentCastRelationship,
  handleCreateCastRelationship,
  onOpenChangeThirdPersona,
  onEndReached,
  hasCastRelationshipTicket
}: PreviousCastRelationshipListProps) => {
  const { t } = useTranslation();
  const thirdPersonaName = currentCastRelationship?.personC?.name ?? '';

  return (
    <div className="mt-[28px] flex flex-col items-center">
      <button
        type="button"
        className="py-[11px] bg-primary-light text-[14px] font-semibold leading-[17px] text-pure-white rounded-full px-[18px] min-w-[286px]"
        onClick={handleCreateCastRelationship}
      >
        {hasCastRelationshipTicket
          ? t('CastRelationship.useTicket')
          : t('CastRelationship.changeNewCast')}
      </button>
      <button
        type="button"
        className="mt-[20px] text-[12px] font-medium leading-[14px] text-main-dark-3"
        onClick={onOpenChangeThirdPersona}
      >
        {t('CastRelationship.thirdPersona', { nickname: thirdPersonaName || '-' })}
        <span
          className="ml-[8px] underline leading-[14px]"
          style={{
            textUnderlineOffset: '2px'
          }}
        >
          {t('CastRelationship.change')}
        </span>
      </button>
      <p className="text-[15px] font-semibold leading-[18px] text-main-dark-2 mt-[40px] mb-[20px] text-left w-full px-[24px]">
        {t('CastRelationship.previousList')}
      </p>
      <div className="px-[24px] w-full">
        <Virtuoso
          useWindowScroll
          style={{ width: '100%' }}
          data={castRelationships}
          endReached={onEndReached}
          itemContent={(_, castRelationship) => (
            <div className="mb-[10px]">
              <PreviousCastRelationshipItem
                key={castRelationship.castRelationshipId}
                castRelationship={castRelationship}
              />
            </div>
          )}
        />
      </div>
    </div>
  );
};

export default PreviousCastRelationshipList;

const PreviousCastRelationshipItem = ({
  castRelationship
}: {
  castRelationship: CastRelationship;
}) => {
  const { t } = useTranslation();
  const { handleUpdateCastRelationship } = useChangeCurrentCastRelationship({
    castRelationshipId: castRelationship.castRelationshipId
  });
  const { personA, personB } = castRelationship ?? {};

  return (
    <div className="p-[20px] bg-backgroundElevated-dark rounded-[16px] border border-pure-black shadow-[0_0_2px_0_rgba(0,0,0,0.05),0_0_8px_0_rgba(0,0,0,0.07)] w-full flex items-center justify-between">
      <div className="flex items-center gap-[7px]">
        <div className="flex items-center gap-[4px]">
          <ProfileAvatar
            thumbnailUrl={personA?.thumbnailUrl}
            widthAndHeight="27px"
            backgroundColor="#504D7F"
          />
          <p className="text-[12px] font-medium leading-[14px] text-main-dark-2">{personA?.name}</p>
        </div>
        <Xmark size={12} className="text-main-dark-3" />
        <div className="flex items-center gap-[4px]">
          <ProfileAvatar
            thumbnailUrl={personB?.thumbnailUrl}
            widthAndHeight="27px"
            backgroundColor="#504D7F"
          />
          <p className="text-[12px] font-medium leading-[14px] text-main-dark-2">{personB?.name}</p>
        </div>
      </div>
      <button
        type="button"
        className="flex items-center gap-[4px] text-[13px] leading-[16px] text-main-dark-3"
        onClick={handleUpdateCastRelationship}
      >
        {t('CastRelationship.select')}
        <ArrowRightSmall size={20} />
      </button>
    </div>
  );
};
